import React, { useState, useEffect } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Row,
  Col,
  PreviewCard,
  Button,
} from "../../../components/Component";
import { Spinner, Input, Label } from "reactstrap";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";
import PermissionGate from "../../../components/permissions/PermissionGate";

const ScoringConfigPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [isGlobal, setIsGlobal] = useState(true);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.getScoringConfig();
      setConfig(response.config || {});
      setIsGlobal(response.is_global);
    } catch (error) {
      toast.error("Erreur lors du chargement de la configuration");
      setConfig(getEmptyConfig());
    } finally {
      setLoading(false);
    }
  };

  const getEmptyConfig = () => ({
    quality_score: {
      max_total: 100,
      hse_alignment: { max_points: 20, keywords: [] },
      risk_level: { high: 25, medium: 15, low: 5 },
      description_clarity: { thresholds: [] },
      action_proposal: { thresholds: [], min_points: 5 },
      prevention_impact: { type_scores: {}, people_exposed_bonus: 5, max_points: 20 },
    },
    month_score: {
      positive_multiplier: 1.2,
      immediate_action_bonus: 10,
      detailed_proposal_bonus: 15,
      detailed_proposal_min_length: 100,
      max_score: 150,
    },
  });

  const updateConfig = (path, value) => {
    setConfig((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (!cur[k]) cur[k] = {};
        cur = cur[k];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const updateNumericConfig = (path, rawValue, fallback) => {
    const val = rawValue === "" || rawValue === null || rawValue === undefined
      ? ""
      : (typeof fallback === "number" && Number.isInteger(fallback)
          ? parseInt(rawValue, 10)
          : parseFloat(rawValue));
    const num = typeof val === "number" && !isNaN(val) ? val : "";
    updateConfig(path, num);
  };

  const displayNum = (val, fallback) =>
    val === "" ? "" : (val ?? fallback);

  const sanitizeConfigForSave = (cfg) => {
    const def = getEmptyConfig();
    const sanitize = (obj, d) => {
      if (obj == null) return d;
      if (Array.isArray(obj)) {
        return obj.map((item, i) =>
          typeof item === "object" && item !== null
            ? sanitize(item, (d && d[i]) || {})
            : item === "" || (typeof item === "number" && isNaN(item)) ? ((d && d[i]) ?? 0) : item
        );
      }
      if (typeof obj === "object") {
        const out = {};
        for (const k of Object.keys(obj)) {
          const v = obj[k];
          const dv = d && d[k];
          if (v === "" || (typeof v === "number" && isNaN(v))) {
            out[k] = typeof dv === "number" ? dv : (typeof dv === "object" ? sanitize({}, dv) : 0);
          } else if (typeof v === "object" && v !== null) {
            out[k] = sanitize(v, dv);
          } else {
            out[k] = v;
          }
        }
        return out;
      }
      return obj;
    };
    return sanitize(JSON.parse(JSON.stringify(cfg)), def);
  };

  const updateKeywords = (keywordsStr) => {
    const keywords = keywordsStr
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean);
    updateConfig("quality_score.hse_alignment.keywords", keywords);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const sanitized = sanitizeConfigForSave(config);
      await api.updateScoringConfig(sanitized);
      toast.success("Configuration enregistrée");
      loadConfig();
    } catch (error) {
      toast.error(error.message || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Réinitialiser aux valeurs par défaut ?")) return;
    setSaving(true);
    try {
      await api.resetScoringConfig();
      toast.success("Configuration réinitialisée");
      loadConfig();
    } catch (error) {
      toast.error(error.message || "Erreur lors de la réinitialisation");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Head title="Configuration scoring" />
        <Content>
          <Block>
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-2 text-soft">Chargement...</p>
            </div>
          </Block>
        </Content>
      </>
    );
  }

  const qs = config?.quality_score || {};
  const ms = config?.month_score || {};
  const hse = qs.hse_alignment || {};
  const riskLevel = qs.risk_level || {};
  const descClarity = qs.description_clarity || {};
  const actionProp = qs.action_proposal || {};
  const prevImpact = qs.prevention_impact || {};
  const typeScores = prevImpact.type_scores || {};

  return (
    <>
      <Head title="Configuration scoring" />
      <Content>
        <BlockHead size="sm">
          <BlockHeadContent>
            <BlockTitle page>Configuration du scoring des observations</BlockTitle>
            <BlockDes className="text-soft">
              Ajustez les coefficients et paliers pour le calcul des scores qualité et carte du mois. Les modifications s'appliquent aux nouvelles évaluations.
            </BlockDes>
          </BlockHeadContent>
        </BlockHead>

        <Block>
          <Row className="g-gs">
            <Col lg="6">
              <PreviewCard className="card-full">
                <div className="card-title-group">
                  <h6 className="card-title">
                    <Icon name="file-text" className="me-2" />
                    Score qualité (max {qs.max_total ?? 100} pts)
                  </h6>
                </div>
                <div className="card-inner">
                  <Row className="g-3">
                    <Col md="6">
                      <Label>Score max total</Label>
                      <Input
                        type="number"
                        value={displayNum(qs.max_total, 100)}
                        onChange={(e) => updateNumericConfig("quality_score.max_total", e.target.value, 100)}
                        min={1}
                        max={200}
                      />
                    </Col>
                    <Col md="12">
                      <hr className="my-3" />
                      <h6 className="text-soft mb-2">Alignement HSE (mots-clés)</h6>
                      <Label>Mots-clés séparés par des virgules</Label>
                      <Input
                        type="text"
                        value={(hse.keywords || []).join(", ")}
                        onChange={(e) => updateKeywords(e.target.value)}
                        placeholder="sécurité, protection, EPI, risque, prévention, conformité"
                      />
                      <small className="text-soft">Max {hse.max_points ?? 20} pts selon le nombre de mots trouvés</small>
                      <Input
                        type="number"
                        className="mt-1"
                        value={displayNum(hse.max_points, 20)}
                        onChange={(e) => updateNumericConfig("quality_score.hse_alignment.max_points", e.target.value, 20)}
                        min={0}
                        max={50}
                      />
                    </Col>
                    <Col md="12">
                      <hr className="my-3" />
                      <h6 className="text-soft mb-2">Niveau de risque</h6>
                      <Row className="g-2">
                        <Col md="4">
                          <Label>Élevé (pts)</Label>
                          <Input
                            type="number"
                            value={displayNum(riskLevel.high, 25)}
                            onChange={(e) => updateNumericConfig("quality_score.risk_level.high", e.target.value, 25)}
                          />
                        </Col>
                        <Col md="4">
                          <Label>Moyen (pts)</Label>
                          <Input
                            type="number"
                            value={displayNum(riskLevel.medium, 15)}
                            onChange={(e) => updateNumericConfig("quality_score.risk_level.medium", e.target.value, 15)}
                          />
                        </Col>
                        <Col md="4">
                          <Label>Faible (pts)</Label>
                          <Input
                            type="number"
                            value={displayNum(riskLevel.low, 5)}
                            onChange={(e) => updateNumericConfig("quality_score.risk_level.low", e.target.value, 5)}
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col md="12">
                      <hr className="my-3" />
                      <h6 className="text-soft mb-2">Clarté de la description</h6>
                      <p className="small text-soft">Paliers par longueur (caractères min → points)</p>
                      {(descClarity.thresholds || []).map((t, i) => (
                        <Row key={i} className="g-2 mb-2">
                          <Col md="6">
                            <Input
                              type="number"
                              value={t.min_length === "" ? "" : (t.min_length ?? "")}
                              onChange={(e) => {
                                const th = [...(descClarity.thresholds || [])];
                                const v = e.target.value;
                                th[i] = { ...th[i], min_length: v === "" ? "" : (parseInt(v, 10) || 0) };
                                updateConfig("quality_score.description_clarity.thresholds", th);
                              }}
                              placeholder="Caractères min"
                            />
                          </Col>
                          <Col md="6">
                            <Input
                              type="number"
                              value={displayNum(t.points, 0)}
                              onChange={(e) => {
                                const th = [...(descClarity.thresholds || [])];
                                const v = e.target.value;
                                th[i] = { ...th[i], points: v === "" ? "" : (parseFloat(v) || 0) };
                                updateConfig("quality_score.description_clarity.thresholds", th);
                              }}
                              placeholder="Points"
                            />
                          </Col>
                        </Row>
                      ))}
                    </Col>
                    <Col md="12">
                      <hr className="my-3" />
                      <h6 className="text-soft mb-2">Action proposée</h6>
                      <Label>Points minimum si proposition courte</Label>
                      <Input
                        type="number"
                        value={displayNum(actionProp.min_points, 5)}
                        onChange={(e) => updateNumericConfig("quality_score.action_proposal.min_points", e.target.value, 5)}
                      />
                      <p className="small text-soft mt-2">Paliers par longueur de la proposition</p>
                      {(actionProp.thresholds || []).map((t, i) => (
                        <Row key={i} className="g-2 mb-2">
                          <Col md="6">
                            <Input
                              type="number"
                              value={t.min_length === "" ? "" : (t.min_length ?? "")}
                              onChange={(e) => {
                                const th = [...(actionProp.thresholds || [])];
                                const v = e.target.value;
                                th[i] = { ...th[i], min_length: v === "" ? "" : (parseInt(v, 10) || 0) };
                                updateConfig("quality_score.action_proposal.thresholds", th);
                              }}
                            />
                          </Col>
                          <Col md="6">
                            <Input
                              type="number"
                              value={displayNum(t.points, 0)}
                              onChange={(e) => {
                                const th = [...(actionProp.thresholds || [])];
                                const v = e.target.value;
                                th[i] = { ...th[i], points: v === "" ? "" : (parseFloat(v) || 0) };
                                updateConfig("quality_score.action_proposal.thresholds", th);
                              }}
                            />
                          </Col>
                        </Row>
                      ))}
                    </Col>
                    <Col md="12">
                      <hr className="my-3" />
                      <h6 className="text-soft mb-2">Impact prévention</h6>
                      <Row className="g-2">
                        {["dangerous", "near_miss", "non_conformity", "positive"].map((type) => (
                          <Col md="6" key={type}>
                            <Label>{type === "dangerous" ? "Dangereuse" : type === "near_miss" ? "Presqu'accident" : type === "non_conformity" ? "Non-conformité" : "Positive"} (pts)</Label>
                            <Input
                              type="number"
                              value={displayNum(typeScores[type], 0)}
                              onChange={(e) =>
                                updateNumericConfig(`quality_score.prevention_impact.type_scores.${type}`, e.target.value, 0)
                              }
                            />
                          </Col>
                        ))}
                        <Col md="6">
                          <Label>Bonus personnes exposées (pts)</Label>
                          <Input
                            type="number"
                            value={displayNum(prevImpact.people_exposed_bonus, 5)}
                            onChange={(e) => updateNumericConfig("quality_score.prevention_impact.people_exposed_bonus", e.target.value, 5)}
                          />
                        </Col>
                        <Col md="6">
                          <Label>Max points impact</Label>
                          <Input
                            type="number"
                            value={displayNum(prevImpact.max_points, 20)}
                            onChange={(e) => updateNumericConfig("quality_score.prevention_impact.max_points", e.target.value, 20)}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </div>
              </PreviewCard>
            </Col>

            <Col lg="6">
              <PreviewCard className="card-full">
                <div className="card-title-group">
                  <h6 className="card-title">
                    <Icon name="star" className="me-2" />
                    Score carte du mois (max {ms.max_score ?? 150} pts)
                  </h6>
                </div>
                <div className="card-inner">
                  <p className="text-soft mb-4">
                    Bonus et multiplicateurs appliqués au score qualité pour le classement carte du mois.
                  </p>
                  <Row className="g-3">
                    <Col md="12">
                      <Label>Multiplicateur observation positive (×)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={displayNum(ms.positive_multiplier, 1.2)}
                        onChange={(e) => updateNumericConfig("month_score.positive_multiplier", e.target.value, 1.2)}
                      />
                    </Col>
                    <Col md="12">
                      <Label>Bonus action immédiate prise (pts)</Label>
                      <Input
                        type="number"
                        value={displayNum(ms.immediate_action_bonus, 10)}
                        onChange={(e) => updateNumericConfig("month_score.immediate_action_bonus", e.target.value, 10)}
                      />
                    </Col>
                    <Col md="12">
                      <Label>Bonus proposition détaillée (pts)</Label>
                      <Input
                        type="number"
                        value={displayNum(ms.detailed_proposal_bonus, 15)}
                        onChange={(e) => updateNumericConfig("month_score.detailed_proposal_bonus", e.target.value, 15)}
                      />
                    </Col>
                    <Col md="12">
                      <Label>Seuil longueur proposition pour bonus (caractères)</Label>
                      <Input
                        type="number"
                        value={displayNum(ms.detailed_proposal_min_length, 100)}
                        onChange={(e) => updateNumericConfig("month_score.detailed_proposal_min_length", e.target.value, 100)}
                      />
                    </Col>
                    <Col md="12">
                      <Label>Score max carte du mois</Label>
                      <Input
                        type="number"
                        value={displayNum(ms.max_score, 150)}
                        onChange={(e) => updateNumericConfig("month_score.max_score", e.target.value, 150)}
                      />
                    </Col>
                  </Row>
                </div>
              </PreviewCard>
            </Col>
          </Row>

          <div className="mt-4 pt-3 border-top d-flex justify-content-end">
            <PermissionGate permission={["Configuration scoring - Modifier"]}>
              <Button color="light" onClick={handleReset} className="me-2" disabled={saving}>
                <Icon name="refresh" className="me-1" />
                Réinitialiser
              </Button>
              <Button color="primary" onClick={handleSave} disabled={saving}>
                {saving ? <Spinner size="sm" /> : <Icon name="save" className="me-1" />}
                Enregistrer
              </Button>
            </PermissionGate>
          </div>
        </Block>
      </Content>
    </>
  );
};

export default ScoringConfigPage;
